// @ts-check

/**
 * A light-weight alternative to grammar lookup tables
 * @param {string} singular
 * @param {string} pluralOption
 * @returns {function(number): string}
 */
function pluralize(singular, pluralOption) {
  const plural = pluralOption || singular + 's'
  return function (count) {
    if (count === 1) {
      return singular
    }
    return plural
  }
}

// https://en.wikipedia.org/wiki/International_System_of_Units#Prefixes
const SI_UNITS = ['', 'k', 'M', 'G', 'T', 'P', 'E']

/**
 * Used for posts/followers/subscriber counts in the UI.
 * @param {number} count
 * @returns {string} Examples: `2k`, `1.1M`
 */
function formatCount(count) {
  const unitTier = Math.min(
    Math.floor(Math.log10(Math.abs(count)) / 3),
    SI_UNITS.length - 1
  )
  if (!count || !unitTier) {
    return `${count}`
  }
  const unitScale = Math.pow(10, unitTier * 3)
  // Work-around undesirable rounding behavior of Number.prototype.toFixed
  const formatted = `${count / unitScale}`
  .replace(/^([0-9]{2,})\.[0-9]+$/, '$1')
  .replace(/(\.[0-9])[0-9]*$/, '$1')
  .replace(/\.0$/, '')
  return formatted + SI_UNITS[unitTier]
}

/**
 * Converts a base64-encoded String to a binary-encoded String.
 * @param {string} data
 * @returns {string}
 */
function decodeBase64(data) {
  if (typeof window === 'object' && typeof window.atob === 'function') {
    // Browser API
    return window.atob(data)
  }
  if (typeof Buffer === 'function') {
    // Node API
    return Buffer.from(data, 'base64').toString('binary')
  }
  throw new Error('No native base64 decoder found')
}

/**
 * Convert a UTF-16 string to an `Array` of UTF-8 code-points.
 *
 * UTF-16 is the default encoding in JavaScript, but is
 * not suitable for transferring over-the-wire.
 * @param {string} utf16Str UTF-16 encoded string
 * @returns {Array.<number>} `Array` of UTF-8 code-points
 * @see https://stackoverflow.com/a/18729931
 */
function stringToUtf8ByteArray(utf16Str) {
  const bytes = []
  for (let i = 0; i < utf16Str.length; i++) {
    let charcode = utf16Str.charCodeAt(i)
    if (charcode < 0x80) {
      bytes.push(charcode)
    } else if (charcode < 0x800) {
      bytes.push(
        0xc0 | (charcode >> 6),
        0x80 | (charcode & 0x3f)
      )
    } else if (charcode < 0xd800 || charcode >= 0xe000) {
      bytes.push(
        0xe0 | (charcode >> 12),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f)
      )
    } else {
      i++ // Surrogate-pair of codepoints
      // UTF-16 encodes 0x10000-0x10FFFF by
      // subtracting 0x10000 and splitting the
      // 20 bits of 0x0-0xFFFFF into two halves
      charcode = 0x10000 + (
        ((charcode & 0x3ff) << 10) |
        (utf16Str.charCodeAt(i) & 0x3ff)
      )
      bytes.push(
        0xf0 | (charcode >> 18),
        0x80 | ((charcode >> 12) & 0x3f),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f)
      )
    }
  }
  return bytes
}

module.exports = {
  decodeBase64,
  formatCount,
  pluralize,
  stringToUtf8ByteArray
}
